import { useMemo, useState } from "react";
import axios from "axios";
import githubLogo from "../CloudGraph/assets/github-logo.svg";
import gitlabLogo from "../CloudGraph/assets/gitlab-logo.svg";
import gcpLogo from "../CloudGraph/assets/gcp-logo.svg";
import awsLogo from "../CloudGraph/assets/aws-logo.svg";
import azureLogo from "../CloudGraph/assets/azure-logo.svg";
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Clock3,
  GitBranch,
  Link2,
  Network,
  ShieldAlert,
  Server,
  Cloud,
  FileText,
  Activity,
  Database
} from "lucide-react";
import { useQuery } from "react-query";
import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../ui/tabs";
import CloudEntityActivityLogs from "./CloudEntityActivityLogs";

interface CloudCorrelation {
  id: number;
  cloudType?: string;
  severity?: string;
  entity?: string;
  correlationKey?: string;
  status?: string;
  occurredAt?: string;
  entityAssociated?: string[];
  correlation?: Record<string, unknown>;
  info?: {
    summary?: string;
    timeDeltaSeconds?: number;
    saasType?: string;
    cloudEventTimeUtc?: string;
    saasEventTimeUtc?: string;
    cloudLog?: Record<string, unknown>;
    saasLog?: Record<string, unknown>;
    context?: Record<string, unknown>;
  };
}

const ITEMS_PER_PAGE = 5;

const normalize = (value: unknown) =>
  String(value ?? "")
    .trim()
    .toLowerCase();

const formatDateTime = (value?: string) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString("en-GB");
};

const getSeverityBadgeClass = (severity?: string) => {
  switch ((severity || "").toUpperCase()) {
    case "CRITICAL":
      return "bg-red-600 text-white hover:bg-red-600";
    case "HIGH":
      return "bg-orange-500 text-white hover:bg-orange-500";
    case "MEDIUM":
      return "bg-amber-500 text-white hover:bg-amber-500";
    case "LOW":
      return "bg-blue-500 text-white hover:bg-blue-500";
    default:
      return "bg-slate-500 text-white hover:bg-slate-500";
  }
};

export default function CloudCorrelations({
  url,
  entityIdentifiers,
}: {
  url: string;
  entityIdentifiers: string[];
}) {
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  const normalizedIdentifiers = useMemo(
    () =>
      Array.from(
        new Set(
          entityIdentifiers
            .map((value) => normalize(value))
            .filter(Boolean)
        )
      ),
    [entityIdentifiers]
  );

  const {
    data: correlations = [],
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["cloud-saas-correlations", url],
    queryFn: async () => {
      const { data } = await axios.get(url);
      return Array.isArray(data) ? data : [];
    },
  });

  const filteredCorrelations = useMemo(() => {
    if (normalizedIdentifiers.length === 0) return correlations;

    return (correlations as CloudCorrelation[]).filter((item) => {
      const matchesEntity = normalizedIdentifiers.includes(normalize(item.entity));
      const matchesAssociated = (item.entityAssociated || []).some((associated) =>
        normalizedIdentifiers.includes(normalize(associated))
      );

      return matchesEntity || matchesAssociated;
    });
  }, [correlations, normalizedIdentifiers]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredCorrelations.length / ITEMS_PER_PAGE)
  );
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedCorrelations = filteredCorrelations.slice(
    startIndex,
    startIndex + ITEMS_PER_PAGE
  );

  const toggleExpanded = (id: number) => {
    const next = new Set(expandedRows);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setExpandedRows(next);
  };

  return (
    <Card className="cea-card-surface">
      <CardHeader className="border-b">
        <CardTitle className="flex items-center gap-2 text-lg font-semibold">
          <Network className="w-5 h-5" />
          Correlations
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Cloud to SaaS correlations matched to the selected entity.
        </p>
      </CardHeader>
      <CardContent className="pt-6">
        {isLoading && (
          <div className="space-y-3">
            {Array.from({ length: ITEMS_PER_PAGE }).map((_, index) => (
              <div
                key={index}
                className="h-28 rounded-lg border animate-pulse bg-muted/40"
              />
            ))}
          </div>
        )}

        {isError && (
          <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-6 text-sm text-destructive">
            Failed to load correlations.
          </div>
        )}

        {!isLoading && !isError && filteredCorrelations.length === 0 && (
          <div className="rounded-lg border px-4 py-8 text-center text-sm text-muted-foreground">
            No correlations found for this entity.
          </div>
        )}

        {!isLoading && !isError && filteredCorrelations.length > 0 && (
          <div className="space-y-4">
            {paginatedCorrelations.map((item) => {
              const isExpanded = expandedRows.has(item.id);

              return (
                <div
                  key={item.id}
                  className="cea-inner-surface overflow-hidden"
                >
                  <button
                    type="button"
                    onClick={() => toggleExpanded(item.id)}
                    className="w-full px-5 py-4 text-left transition-colors hover:bg-muted/30"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 space-y-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge className={getSeverityBadgeClass(item.severity)}>
                              {item.severity || "UNKNOWN"}
                            </Badge>
                            {item.info?.saasType && (
                              <Badge variant="outline" className="bg-emerald-500/10 text-emerald-600 border-emerald-500/20 dark:text-emerald-400">
                                {item.info.saasType}
                              </Badge>
                            )}
                            <Badge variant="secondary" className="flex items-center gap-1 font-normal">
                              <Clock3 className="w-3 h-3" />
                              {item.info?.timeDeltaSeconds ?? "—"}s delta
                            </Badge>
                          </div>
                          <div className="text-xs text-muted-foreground whitespace-nowrap hidden sm:flex items-center">
                             {formatDateTime(item.occurredAt)}
                          </div>
                        </div>

                        <div className="space-y-1.5">
                          <div className="text-sm font-medium leading-relaxed text-foreground line-clamp-2">
                            {item.info?.summary || item.correlationKey || "Correlation"}
                          </div>
                          <div className="mt-2 flex items-center gap-3 flex-wrap text-sm text-muted-foreground pt-1">
                            <span className="flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-muted/50 border border-border">
                              <ShieldAlert className="w-3.5 h-3.5" />
                              {item.entity || "—"}
                            </span>
                          </div>
                        </div>

                        {(item.entityAssociated || []).length > 0 && (
                          <div className="flex items-center gap-2 flex-wrap pt-0.5">
                            <span className="text-xs text-muted-foreground mr-1">Associated Resources:</span>
                            {(item.entityAssociated || []).map((associated: string) => (
                              <Badge key={associated} variant="outline" className="text-xs font-normal text-muted-foreground">
                                {associated}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>

                      <div className="pt-1 text-muted-foreground flex flex-col justify-center items-center h-full my-auto ml-2">
                        <div className={`p-2 rounded-full transition-colors ${isExpanded ? 'bg-muted text-foreground' : 'hover:bg-muted'}`}>
                          {isExpanded ? (
                            <ChevronUp className="w-4 h-4" />
                          ) : (
                            <ChevronDown className="w-4 h-4" />
                          )}
                        </div>
                      </div>
                    </div>
                  </button>

                  {isExpanded && (
                    <div className="border-t bg-muted/10 px-5 py-5 space-y-6">
                      {/* Visual Timeline */}
                      <div className="cea-inner-surface relative flex flex-col md:flex-row items-center justify-between gap-4 p-5">
                        
                        {/* Cloud Side */}
                        <div className="flex-1 flex flex-col items-center md:items-start text-center md:text-left z-10 w-full md:w-auto">
                          <div className="flex items-center gap-2 mb-2">
                            <div className="p-2 bg-blue-500/10 text-blue-600 dark:text-blue-400 rounded-lg shadow-sm flex items-center justify-center">
                               {item.cloudType === 'GCP' ? (
                                  <img src={gcpLogo} alt="GCP" className="w-5 h-5 object-contain" />
                               ) : item.cloudType === 'AWS' ? (
                                  <img src={awsLogo} alt="AWS" className="w-5 h-5 object-contain" />
                               ) : item.cloudType === 'AZURE' ? (
                                  <img src={azureLogo} alt="Azure" className="w-5 h-5 object-contain" />
                               ) : (
                                  <Cloud className="w-5 h-5" />
                               )}
                            </div>
                            <div>
                              <div className="font-semibold text-sm">{item.cloudType || 'Cloud Environment'}</div>
                              <div className="text-xs text-muted-foreground">{formatDateTime(item.info?.cloudEventTimeUtc)}</div>
                            </div>
                          </div>
                          <div className="text-xs font-mono mt-2 w-full bg-background p-2.5 rounded-md border border-border flex items-center gap-2 shadow-sm truncate" title={String(item.info?.context?.cloudAccountId || '')}>
                            <FileText className="w-3.5 h-3.5 text-blue-500" />
                            <span className="truncate">{item.info?.context?.cloudAccountId || 'Unknown Account'}</span>
                          </div>
                        </div>

                        {/* Connection */}
                        <div className="hidden md:flex flex-col items-center flex-1 z-10 px-4 mt-8 md:mt-0">
                           <div className="text-xs font-medium bg-muted/60 text-muted-foreground px-3 py-1 rounded-full mb-3 flex items-center gap-1.5 border">
                             <Clock3 className="w-3.5 h-3.5" />
                             {item.info?.timeDeltaSeconds}s delta
                           </div>
                           <div className="w-full relative flex items-center justify-center">
                             <div className="absolute w-full h-[2px] bg-gradient-to-r from-blue-500/50 via-purple-500/50 to-emerald-500/50"></div>
                             <div className="bg-background border shadow-sm rounded-full p-2 z-10">
                               <Link2 className="w-4 h-4 text-foreground" />
                             </div>
                           </div>
                        </div>
                        {/* Mobile connection */}
                        <div className="md:hidden flex flex-col items-center my-3 w-full">
                           <div className="w-[2px] h-8 bg-gradient-to-b from-blue-500/50 to-emerald-500/50"></div>
                           <div className="text-xs font-medium bg-muted/60 text-muted-foreground px-3 py-1 rounded-full my-2 flex items-center gap-1.5 border shadow-sm">
                             <Clock3 className="w-3 h-3" />
                             {item.info?.timeDeltaSeconds}s delta
                           </div>
                           <div className="w-[2px] h-8 bg-gradient-to-b from-blue-500/50 to-emerald-500/50"></div>
                        </div>

                        {/* SaaS Side */}
                        <div className="flex-1 flex flex-col items-center md:items-end text-center md:text-right z-10 w-full md:w-auto">
                          <div className="flex items-center gap-2 mb-2 flex-row-reverse md:flex-row">
                            <div>
                              <div className="font-semibold text-sm">{item.info?.saasType || 'SaaS Environment'}</div>
                              <div className="text-xs text-muted-foreground">{formatDateTime(item.info?.saasEventTimeUtc)}</div>
                            </div>
                            <div className="p-2 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 rounded-lg shadow-sm flex items-center justify-center">
                               {item.info?.saasType === 'GITHUB' ? (
                                  <img src={githubLogo} alt="GitHub" className="w-5 h-5 object-contain" />
                               ) : item.info?.saasType === 'GITLAB' ? (
                                  <img src={gitlabLogo} alt="GitLab" className="w-5 h-5 object-contain" />
                               ) : (
                                  <Server className="w-5 h-5" />
                               )}
                            </div>
                          </div>
                          <div className="text-xs font-mono mt-2 w-full text-left bg-background p-2.5 rounded-md border border-border flex items-center justify-start md:justify-end gap-2 shadow-sm truncate" title={String(item.info?.context?.githubAccount || item.info?.context?.repository || '')}>
                            <span className="truncate">{item.info?.context?.githubAccount || item.info?.context?.repository || 'Unknown Resource'}</span>
                            <Database className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                          </div>
                        </div>
                      </div>

                      {/* Details & Context Grid */}
                      <div className="grid gap-4 md:grid-cols-2">
                        <div className="cea-inner-surface space-y-3 p-5">
                          <div className="flex items-center gap-2 text-sm font-semibold pb-2 border-b border-border/50">
                            <GitBranch className="w-4 h-4 text-purple-500" />
                            Correlation Details
                          </div>
                          <div className="space-y-3 text-sm pt-1">
                            <div className="flex flex-col gap-1">
                              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Correlation Key</span>
                              <span className="break-all font-mono text-xs p-1.5 bg-muted/30 rounded border border-transparent">{item.correlationKey || "—"}</span>
                            </div>
                            <div className="flex flex-col gap-1">
                              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Reason</span>
                              <span className="break-words leading-relaxed text-muted-foreground">
                                {String(item.info?.context?.correlationReason || "—")}
                              </span>
                            </div>
                          </div>
                        </div>

                        <div className="cea-inner-surface space-y-3 p-5">
                          <div className="flex items-center gap-2 text-sm font-semibold pb-2 border-b border-border/50">
                            <Activity className="w-4 h-4 text-orange-500" />
                            Extracted Context
                          </div>
                          <div className="space-y-3 text-sm pt-1">
                            <div className="grid grid-cols-2 gap-x-4 gap-y-4">
                              <div className="flex flex-col gap-1">
                                <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Repository</span>
                                <span className="truncate font-medium" title={String(item.info?.context?.repository || "—")}>{String(item.info?.context?.repository || "—")}</span>
                              </div>
                              <div className="flex flex-col gap-1">
                                <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Secret Name</span>
                                <span className="truncate font-medium" title={String(item.info?.context?.secretName || "—")}>{String(item.info?.context?.secretName || "—")}</span>
                              </div>
                              <div className="flex flex-col gap-1">
                                <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">GitHub Account</span>
                                <span className="truncate font-medium" title={String(item.info?.context?.githubAccount || "—")}>{String(item.info?.context?.githubAccount || "—")}</span>
                              </div>
                              <div className="flex flex-col gap-1">
                                <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Cloud Account</span>
                                <span className="truncate font-medium" title={String(item.info?.context?.cloudAccountId || "—")}>{String(item.info?.context?.cloudAccountId || "—")}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* JSON Logs Tabs */}
                      <div className="cea-inner-surface overflow-hidden pt-2">
                        <Tabs defaultValue="cloud" className="w-full">
                          <div className="px-4 border-b">
                            <TabsList className="bg-transparent h-12 p-0 gap-4">
                              <TabsTrigger value="cloud" className="data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none h-12 px-2">
                                Cloud Log
                              </TabsTrigger>
                              <TabsTrigger value="saas" className="data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none h-12 px-2">
                                SaaS Log
                              </TabsTrigger>
                            </TabsList>
                          </div>
                          
                          <TabsContent value="cloud" className="m-0 border-none p-0 focus-visible:ring-0">
                            <CloudEntityActivityLogs data={item.info?.cloudLog} />
                          </TabsContent>
                          
                          <TabsContent value="saas" className="m-0 border-none p-0 focus-visible:ring-0">
                            <CloudEntityActivityLogs data={item.info?.saasLog} />
                          </TabsContent>
                        </Tabs>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            {filteredCorrelations.length > ITEMS_PER_PAGE && (
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">
                  Showing {startIndex + 1} to{" "}
                  {Math.min(startIndex + ITEMS_PER_PAGE, filteredCorrelations.length)} of{" "}
                  {filteredCorrelations.length} correlations
                </p>

                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Previous
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    {currentPage} / {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setCurrentPage((page) => Math.min(totalPages, page + 1))
                    }
                    disabled={currentPage === totalPages}
                  >
                    Next
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
